import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Upload, FileText, RefreshCw, Search, CheckCircle2, Loader2, Save,
  Printer, Download, Receipt, ScrollText, Filter, Undo2,
} from "lucide-react";
import { toast } from "sonner";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

const mediaUrl = (u) => (u ? `${API_BASE.replace(/\/api$/, "")}${u}` : "");

// ============================================================================
// Purchase Bill — inline image preview + upload/edit/post + print
// ============================================================================
const _mkKey = () =>
  `k_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

function PurchaseLineEditor({ inv, setInv }) {
  const setLine = (i, f, v) => {
    const lines = [...(inv.lines || [])];
    lines[i] = { ...lines[i], [f]: (["quantity", "purchase_rate", "mrp", "discount_pct", "gst_pct", "free_qty"].includes(f))
      ? (v === "" ? null : (parseFloat(v) || 0)) : v };
    setInv({ ...inv, lines });
  };
  const add = () => setInv({ ...inv, lines: [...(inv.lines || []), { _key: _mkKey(), name: "", quantity: 1, purchase_rate: 0, mrp: 0, gst_pct: 12 }] });
  const remove = (i) => setInv({ ...inv, lines: (inv.lines || []).filter((_, k) => k !== i) });
  const display = (v) => (v === null || v === undefined || v === 0 ? "" : String(v));
  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5 items-center px-2 text-[10px] uppercase text-slate-500 font-semibold">
        <div className="w-40">Item</div><div className="w-24">HSN</div><div className="w-24">Batch</div>
        <div className="w-24">Expiry</div><div className="w-16 text-right">Qty</div>
        <div className="w-24 text-right">Purchase Rate</div><div className="w-20 text-right">MRP</div>
        <div className="w-16 text-right">GST%</div><div className="w-24 text-right">Line Total</div>
      </div>
      {(inv.lines || []).map((l, i) => (
        <div key={l._key || l.id || `pb-line-${i}`} className="flex gap-1.5 items-center border rounded p-1.5 text-xs">
          <Input className="w-40 h-8 text-xs" value={l.name || ""} onChange={(e) => setLine(i, "name", e.target.value)} placeholder="Medicine" data-testid={`pb-line-name-${i}`} />
          <Input className="w-24 h-8 text-xs" value={l.hsn_sac || ""} onChange={(e) => setLine(i, "hsn_sac", e.target.value)} placeholder="HSN" />
          <Input className="w-24 h-8 text-xs" value={l.batch_no || ""} onChange={(e) => setLine(i, "batch_no", e.target.value)} placeholder="Batch" />
          <Input className="w-24 h-8 text-xs" value={l.expiry_date || ""} onChange={(e) => setLine(i, "expiry_date", e.target.value)} placeholder="MM/YYYY" />
          <Input className="no-spinner w-16 h-8 text-xs text-right" type="number" value={display(l.quantity)} onChange={(e) => setLine(i, "quantity", e.target.value)} placeholder="Qty" />
          <Input className="no-spinner w-24 h-8 text-xs text-right" type="number" value={display(l.purchase_rate)} onChange={(e) => setLine(i, "purchase_rate", e.target.value)} placeholder="Rate" data-testid={`pb-line-rate-${i}`} />
          <Input className="no-spinner w-20 h-8 text-xs text-right" type="number" value={display(l.mrp)} onChange={(e) => setLine(i, "mrp", e.target.value)} placeholder="MRP" />
          <Input className="no-spinner w-16 h-8 text-xs text-right" type="number" value={display(l.gst_pct)} onChange={(e) => setLine(i, "gst_pct", e.target.value)} placeholder="GST" />
          <div className="w-24 text-right tabular-nums">{inr(l.line_total)}</div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => remove(i)}>×</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="pb-add-line-btn">+ Add row</Button>
    </div>
  );
}

function MediaPreview({ url, mime }) {
  if (!url) return null;
  const full = mediaUrl(url);
  const isPdf = (mime || "").includes("pdf") || url.toLowerCase().endsWith(".pdf");
  return (
    <div className="border rounded bg-slate-50 overflow-hidden">
      <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] bg-slate-100 border-b">
        <FileText size={12} /> <span className="font-mono truncate">{url}</span>
        <a href={full} target="_blank" rel="noreferrer" className="ml-auto text-primary underline text-xs" data-testid="pb-media-open">Open</a>
      </div>
      {isPdf ? (
        <embed src={full} type="application/pdf" className="w-full h-72" />
      ) : (
        <img src={full} alt="uploaded bill" className="w-full max-h-72 object-contain bg-white" data-testid="pb-media-image" />
      )}
    </div>
  );
}

function PurchaseTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [vendor, setVendor] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/purchase/list`, {
        params: { vendor: vendor || undefined, status: status || undefined },
      });
      setRows(data.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, [vendor, status]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/purchase/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded — ${data.id} (draft)`);
      setEditing(data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    setBusy("save");
    try {
      const { data } = await api.patch(`/purchase/${editing.id}`, {
        invoice_no: editing.invoice_no, invoice_date: editing.invoice_date,
        vendor_name: editing.vendor_name, vendor_gstin: editing.vendor_gstin,
        vendor_dl: editing.vendor_dl, vendor_address: editing.vendor_address,
        place_of_supply: editing.place_of_supply, notes: editing.notes,
        lines: editing.lines,
      });
      setEditing(data);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const post = async () => {
    if (!editing?.id) return;
    if (!window.confirm("Post this bill to inventory? Posted bills are read-only.")) return;
    setBusy("post");
    try {
      const { data } = await api.post(`/purchase/${editing.id}/post`);
      setEditing(data);
      toast.success("Bill posted → SKU catalogue updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setBusy(""); }
  };

  const printBill = (row) => {
    if (row?.media_url) {
      window.open(mediaUrl(row.media_url), "_blank");
    } else {
      toast.error("No file attached to print");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Upload supplier invoices — AI extracts vendor, HSN, batch, expiry, MRP, rate. Draft → staff confirms → posted to inventory.
        </p>
        <div className="flex gap-2 items-center">
          <label className="cursor-pointer">
            <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="pb-upload-input"
                   onChange={(e) => upload(e.target.files?.[0])} />
            <span className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm">
              {busy === "upload" ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload bill
            </span>
          </label>
          <Button variant="outline" size="sm" onClick={load} data-testid="pb-refresh-btn"><RefreshCw size={14} /></Button>
        </div>
      </div>

      <Card><CardContent className="p-3 flex gap-2 items-center flex-wrap">
        <Search size={14} className="text-slate-500" />
        <Input className="w-64 h-9" placeholder="Vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="pb-filter-vendor" />
        <select className="h-9 border rounded px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pb-filter-status">
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="posted">Posted</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <Button size="sm" onClick={load}>Apply</Button>
      </CardContent></Card>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vendor</TableHead>
              <TableHead>Invoice No</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No bills yet — upload the first one.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`pb-row-${r.id}`}>
                <TableCell>
                  <div className="text-sm font-medium">{r.vendor_name || "—"}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.id}</div>
                </TableCell>
                <TableCell className="text-xs">{r.invoice_no || "—"}</TableCell>
                <TableCell className="text-xs">{r.invoice_date || (r.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{inr(r.grand_total)}</TableCell>
                <TableCell><Badge className={r.status === "posted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{r.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => printBill(r)} disabled={!r.media_url} title="Print / view original" data-testid={`pb-print-${r.id}`}>
                      <Printer size={12} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`pb-edit-${r.id}`}>Review</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {editing && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">Bill {editing.id}</h2>
            <Badge className={editing.status === "posted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{editing.status}</Badge>
            <Button size="sm" variant="outline" onClick={() => printBill(editing)} disabled={!editing.media_url} data-testid="pb-print-editing">
              <Printer size={12} /> Print
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setEditing(null)}>Close</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><label className="text-slate-500">Vendor Name</label><Input value={editing.vendor_name || ""} onChange={(e) => setEditing({ ...editing, vendor_name: e.target.value })} data-testid="pb-vendor-name" /></div>
                <div><label className="text-slate-500">GSTIN</label><Input value={editing.vendor_gstin || ""} onChange={(e) => setEditing({ ...editing, vendor_gstin: e.target.value })} data-testid="pb-vendor-gstin" /></div>
                <div><label className="text-slate-500">DL Number</label><Input value={editing.vendor_dl || ""} onChange={(e) => setEditing({ ...editing, vendor_dl: e.target.value })} /></div>
                <div><label className="text-slate-500">Place of Supply</label><Input value={editing.place_of_supply || ""} onChange={(e) => setEditing({ ...editing, place_of_supply: e.target.value })} /></div>
                <div className="col-span-2"><label className="text-slate-500">Address</label><Input value={editing.vendor_address || ""} onChange={(e) => setEditing({ ...editing, vendor_address: e.target.value })} /></div>
                <div><label className="text-slate-500">Invoice No</label><Input value={editing.invoice_no || ""} onChange={(e) => setEditing({ ...editing, invoice_no: e.target.value })} data-testid="pb-invoice-no" /></div>
                <div><label className="text-slate-500">Invoice Date</label><Input type="date" value={editing.invoice_date || ""} onChange={(e) => setEditing({ ...editing, invoice_date: e.target.value })} data-testid="pb-invoice-date" /></div>
                <div className="col-span-2"><label className="text-slate-500">Notes</label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
              </div>
            </div>
            <MediaPreview url={editing.media_url} mime={editing.mime_type} />
          </div>

          <PurchaseLineEditor inv={editing} setInv={setEditing} />

          <div className="grid grid-cols-5 gap-2 text-xs border-t pt-2">
            <div><div className="text-[10px] text-slate-500">Subtotal</div>{inr(editing.subtotal)}</div>
            <div><div className="text-[10px] text-slate-500">Discount</div>{inr(editing.discount_total)}</div>
            <div><div className="text-[10px] text-slate-500">Taxable</div>{inr(editing.taxable_amount)}</div>
            <div><div className="text-[10px] text-slate-500">GST</div>{inr(editing.gst_total)}</div>
            <div className="text-emerald-800"><div className="text-[10px]">Grand total</div><div className="font-bold">{inr(editing.grand_total)}</div></div>
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit} disabled={busy === "save" || editing.status === "posted"} data-testid="pb-save-btn">
              {busy === "save" ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
            </Button>
            <Button size="sm" onClick={post} disabled={busy === "post" || editing.status === "posted"} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pb-post-btn">
              {busy === "post" ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Post to inventory
            </Button>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}


// ============================================================================
// Sales Bill / Sales Report — full filter UI wired to backend /billing/sales
// ============================================================================
function SalesTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [grandSum, setGrandSum] = useState(0);
  const [paidSum, setPaidSum] = useState(0);
  const [outstandingSum, setOutstandingSum] = useState(0);
  const [tdsSum, setTdsSum] = useState(0);
  const [taxableSum, setTaxableSum] = useState(0);
  const [gstSum, setGstSum] = useState(0);
  const [busy, setBusy] = useState(false);
  // v1.63 — Refund dialog state
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", reason: "", reference: "" });
  const [refunding, setRefunding] = useState(false);

  const openRefund = (r) => {
    setRefundTarget(r);
    setRefundForm({ amount: String(r.paid_amount || r.grand_total || r.amount || ""),
                    reason: "", reference: "" });
  };
  const confirmRefund = async () => {
    if (!refundTarget) return;
    const invoiceId = refundTarget.invoice_id || refundTarget.order_id;
    const amt = parseFloat(refundForm.amount || 0);
    if (!amt || amt <= 0) { toast.error("Enter refund amount"); return; }
    setRefunding(true);
    try {
      const r = await api.post(`/accounts/invoices/${invoiceId}/refund`, {
        amount: amt,
        reason: refundForm.reason,
        reference: refundForm.reference,
      });
      toast.success(`Refund processed via ${r.data?.refund_provider || "manual"} · ${inr(amt)}`);
      setRefundTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  const emptyFilters = {
    from: "", to: "", customer_category: "all", insurance_status: "all",
    service_type: "all", payment_status: "all", payment_mode: "all",
    payment_gateway: "all", bk_centre_uid: "", insurance_unique_id: "",
    invoice_no: "",
  };
  const [f, setF] = useState(emptyFilters);
  const setField = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const resetFilters = () => setF(emptyFilters);

  const buildParams = () => {
    const p = { only_invoiced: false, limit: 200 };
    if (f.from) p.from = f.from;
    if (f.to) p.to = f.to;
    if (f.customer_category && f.customer_category !== "all") p.customer_category = f.customer_category;
    if (f.insurance_status && f.insurance_status !== "all") p.insurance_status = f.insurance_status;
    if (f.service_type && f.service_type !== "all") p.service_type = f.service_type;
    if (f.payment_status && f.payment_status !== "all") p.payment_status = f.payment_status;
    if (f.payment_mode && f.payment_mode !== "all") p.payment_mode = f.payment_mode;
    if (f.payment_gateway && f.payment_gateway !== "all") p.payment_gateway = f.payment_gateway;
    if (f.bk_centre_uid) p.bk_centre_uid = f.bk_centre_uid;
    if (f.insurance_unique_id) p.insurance_unique_id = f.insurance_unique_id;
    if (f.invoice_no) p.invoice_no = f.invoice_no;
    return p;
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/billing/sales`, { params: buildParams() });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setGrandSum(data.grand_sum || 0);
      setPaidSum(data.paid_sum || 0);
      setOutstandingSum(data.outstanding_sum || 0);
      setTdsSum(data.tds_sum || 0);
      setTaxableSum(data.taxable_sum || 0);
      setGstSum(data.gst_sum || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const download = () => {
    const q = new URLSearchParams();
    Object.entries(buildParams()).forEach(([k, v]) => q.append(k, String(v)));
    window.open(`${API_BASE}/billing/sales.csv?${q.toString()}`, "_blank");
  };

  const printInvoice = (row) => {
    if (row?.invoice_pdf) {
      window.open(`${API_BASE}/order-mgmt/orders/${row.order_id}/invoice/pdf`, "_blank");
    } else {
      toast.error("No invoice PDF yet — generate the invoice first");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Sales Report / Export Data — filter server-side, then download the exact same records as CSV.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="sb-apply">
            {busy ? <Loader2 className="animate-spin" size={12} /> : <Filter size={12} />} Apply Filters
          </Button>
          <Button size="sm" variant="outline" onClick={resetFilters} data-testid="sb-reset">
            <RefreshCw size={12} /> Reset
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={download} data-testid="sb-download-csv">
            <Download size={12} /> Download CSV
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <div>
          <label className="text-[10px] text-slate-500">Customer Category</label>
          <select value={f.customer_category} onChange={(e) => setField("customer_category", e.target.value)} data-testid="sb-customer-category" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All / Both</option>
            <option value="teachers">BK Teachers</option>
            <option value="students">BK Students</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Teacher Insurance Status</label>
          <select value={f.insurance_status} onChange={(e) => setField("insurance_status", e.target.value)} data-testid="sb-insurance-status" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All</option>
            <option value="insured">Insured</option>
            <option value="uninsured">Non-Insured</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">From (Invoice Date)</label>
          <Input type="date" value={f.from} onChange={(e) => setField("from", e.target.value)} className="h-9" data-testid="sb-from" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500">To (Invoice Date)</label>
          <Input type="date" value={f.to} onChange={(e) => setField("to", e.target.value)} className="h-9" data-testid="sb-to" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Service Type</label>
          <select value={f.service_type} onChange={(e) => setField("service_type", e.target.value)} data-testid="sb-service-type" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All</option>
            <option value="medicine">Medicine</option>
            <option value="lab_test">Lab Test</option>
            <option value="full_body_checkup">Full Body Checkup</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Payment Status</label>
          <select value={f.payment_status} onChange={(e) => setField("payment_status", e.target.value)} data-testid="sb-payment-status" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Payment Mode</label>
          <select value={f.payment_mode} onChange={(e) => setField("payment_mode", e.target.value)} data-testid="sb-payment-mode" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="credit_card">Credit Card</option>
            <option value="debit_card">Debit Card</option>
            <option value="netbanking">Netbanking</option>
            <option value="wallet">Wallet</option>
            <option value="emi">EMI</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="manual">Manual / Offline</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Payment Gateway</label>
          <select value={f.payment_gateway} onChange={(e) => setField("payment_gateway", e.target.value)} data-testid="sb-payment-gateway" className="h-9 w-full rounded border px-2 text-sm">
            <option value="all">All</option>
            <option value="razorpay">Razorpay</option>
            <option value="cashfree">Cashfree</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500">BK Centre UID</label>
          <Input value={f.bk_centre_uid} onChange={(e) => setField("bk_centre_uid", e.target.value)} className="h-9" data-testid="sb-centre-uid" placeholder="Exact match" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Insurance Unique ID</label>
          <Input value={f.insurance_unique_id} onChange={(e) => setField("insurance_unique_id", e.target.value)} className="h-9" data-testid="sb-insurance-id" placeholder="Exact match" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500">Invoice No.</label>
          <Input value={f.invoice_no} onChange={(e) => setField("invoice_no", e.target.value)} className="h-9" data-testid="sb-invoice-no" placeholder="MSI/…" />
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Records</div><div className="text-xl font-bold">{total}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Invoice Total</div><div className="text-xl font-bold text-emerald-800">{inr(grandSum)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Paid</div><div className="text-xl font-bold">{inr(paidSum)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Outstanding</div><div className="text-xl font-bold text-amber-800">{inr(outstandingSum)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">Taxable</div><div className="text-xl font-bold">{inr(taxableSum)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-xs text-slate-500">GST / TDS</div><div className="text-sm font-bold">{inr(gstSum)} / <span className="text-slate-500">{inr(tdsSum)}</span></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill Number</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>User Category</TableHead>
              <TableHead>Centre UID / Ins ID</TableHead>
              <TableHead>Service</TableHead>
              <TableHead className="text-right">Invoice Total</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No records match these filters.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.order_id} data-testid={`sb-row-${r.order_id}`}>
                <TableCell>
                  <div className="font-mono text-xs text-primary">{r.invoice_number || "—"}</div>
                  <div className="text-[10px] text-slate-500">{r.order_number}</div>
                </TableCell>
                <TableCell className="text-xs">{r.date}</TableCell>
                <TableCell>
                  <div className="text-sm">{r.customer_name || "—"}</div>
                  <div className="text-[10px] text-slate-500">{r.phone}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge variant="outline" className="w-fit text-[10px]">{r.category_label}</Badge>
                    {r.is_bk_teacher && <Badge className="w-fit text-[10px] bg-emerald-100 text-emerald-800">{r.insurance_status || "BK Teacher"}</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  <div>{r.bk_centre_uid || "—"}</div>
                  <div className="text-[10px] text-slate-500">{r.insurance_unique_id || ""}</div>
                </TableCell>
                <TableCell className="text-xs">{r.order_type}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{inr(r.amount)}</TableCell>
                <TableCell>
                  {/* Unified payment cell — auto-populated from Razorpay webhook,
                      NEVER edited by hand. Small team = zero manual data entry. */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Badge className={r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid={`sb-status-${r.order_id}`}>
                        {r.payment_status || "—"}
                      </Badge>
                      {r.payment_status === "paid" && (
                        <span className="text-[10px] text-emerald-800 font-medium">{inr(r.paid_amount)}</span>
                      )}
                    </div>
                    {r.outstanding_amount > 0 && (
                      <div className="text-[10px] text-amber-700">Outstanding: <b>{inr(r.outstanding_amount)}</b></div>
                    )}
                    {r.payment_mode ? (
                      <Badge className="bg-slate-100 text-slate-800 w-fit uppercase text-[10px]" data-testid={`sb-mode-${r.order_id}`}>
                        {r.payment_mode}
                      </Badge>
                    ) : r.payment_status === "paid" ? (
                      <span className="text-[10px] text-slate-400 italic">method not yet synced</span>
                    ) : null}
                    {r.payment_gateway && (
                      <div className="text-[10px] text-slate-500">
                        {r.payment_gateway}{r.transaction_id ? ` · ${r.transaction_id}` : ""}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => printInvoice(r)} disabled={!r.invoice_pdf} data-testid={`sb-print-${r.order_id}`}>
                      <Printer size={12} /> Print
                    </Button>
                    {r.payment_status === "paid" && !r.refund_status && (
                      <Button size="sm" variant="outline"
                              onClick={() => openRefund(r)}
                              data-testid={`sb-refund-${r.order_id}`}
                              className="border-red-300 text-red-700 hover:bg-red-50">
                        <Undo2 size={12} /> Refund
                      </Button>
                    )}
                    {r.refund_status === "processed" && (
                      <Badge className="bg-red-100 text-red-800" data-testid={`sb-refunded-${r.order_id}`}>
                        Refunded {inr(r.refund_amount)}
                      </Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!refundTarget} onOpenChange={(v) => !v && !refunding && setRefundTarget(null)}>
        <DialogContent data-testid="sb-refund-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Refund invoice</DialogTitle>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border p-3 bg-slate-50 text-sm">
                <div><b>{refundTarget.invoice_number || "—"}</b> · {refundTarget.customer_name || "—"}</div>
                <div className="text-xs text-slate-600">Paid: {inr(refundTarget.paid_amount)} · Grand: {inr(refundTarget.grand_total || refundTarget.amount)}</div>
                {refundTarget.transaction_id && (
                  <div className="text-[10px] text-slate-500 font-mono mt-1">{refundTarget.payment_gateway} · {refundTarget.transaction_id}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-600">Refund amount (₹)</label>
                <Input type="number" value={refundForm.amount}
                       onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                       data-testid="sb-refund-amount" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Reason</label>
                <Input value={refundForm.reason} placeholder="e.g. Duplicate charge / customer request"
                       onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                       data-testid="sb-refund-reason" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Manual reference (used if provider API isn't configured)</label>
                <Input value={refundForm.reference} placeholder="Bank UTR / cheque / cash memo"
                       onChange={(e) => setRefundForm({ ...refundForm, reference: e.target.value })}
                       data-testid="sb-refund-reference" />
              </div>
              <div className="text-[11px] text-slate-500">
                Tries Cashfree/Razorpay refund first, falls back to manual if API unavailable. Any TDS deducted on this invoice will be tagged refunded in the analytics report.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={refunding} data-testid="sb-refund-cancel">
              Cancel
            </Button>
            <Button onClick={confirmRefund} disabled={refunding}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="sb-refund-confirm">
              {refunding ? <Loader2 className="animate-spin" size={14} /> : <Undo2 size={14} />}
              Process refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// Unified Billing page — tabs
// ============================================================================
export default function Billing() {
  const [tab, setTab] = useState("purchase");
  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Billing</h1>
        <p className="text-sm text-muted-foreground">All purchase and sales invoices in one place. Upload supplier bills, review AI-extracted data, and export BK-Teacher payment ledgers.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="purchase" data-testid="tab-purchase">
            <Receipt size={14} className="mr-1.5" /> Purchase Bill
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">
            <ScrollText size={14} className="mr-1.5" /> Sales Bill
          </TabsTrigger>
        </TabsList>
        <TabsContent value="purchase" className="mt-0">
          <PurchaseTab />
        </TabsContent>
        <TabsContent value="sales" className="mt-0">
          <SalesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
